import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { DEFAULT_PRICE_CURRENCY } from "shared/constants/app-constants"
import { exists } from "shared/lib/exists"
import { FormattedPriceGroups } from "shared/ui/formatted-price-groups/FormattedPriceGroups"
import { FormattedPrice } from "shared/ui/formatted-price/FormattedPrice"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { InfoTip } from "shared/ui/info-tip/InfoTip"
import { Readonly } from "shared/ui/readonly/Readonly"

import { useProductPageContext } from "../context/ProductPageContext"
import { ProductPageConfigureButton } from "./ProductPageConfigureButton"
import { ProductPageDimensionButton } from "./ProductPageDimensionButton"
import { ProductPageDownloadButton } from "./ProductPageDownloadButton"
import { ProductPageFavoriteButton } from "./ProductPageFavoriteButton"
import { ProductPageImage } from "./ProductPageImage"
import { ProductPageVariantKeyDisplay } from "./ProductPageVariantKeyDisplay"

interface ProductPageSummaryProps
  extends React.HTMLAttributes<HTMLDivElement> {}

export const ProductPageSummary = React.memo(
  React.forwardRef<HTMLDivElement, ProductPageSummaryProps>(
    ({ className, children, ...props }, ref) => {
      const { t } = useTranslation()
      const { productData } = useProductPageContext()
      // const deliveryTime = isDeliveryTime(
      //   productData.productInformation?.data?.deliveryTime
      // )
      //   ? productData.productInformation?.data?.deliveryTime
      //   : null

      // const formattedDeliveryTime = useFormattedDeliveryTime(deliveryTime)
      const price = productData.productInformation?.data?.price

      return (
        <section
          {...props}
          ref={ref}
          className={classNames(
            "product-page__summary product-page__section card card--elevated",
            className
          )}
        >
          <div className="product-page__section-header">
            {/*
              h2, not h3: this is the page's first heading and a sibling of the
              product section's own h2, so an h3 here skips a level. The class
              carries the styling, so the visual size is unchanged.
            */}
            <h2 className="product-page__section-title">
              {t("ProductPage.YourProduct")}
            </h2>
            <ProductPageFavoriteButton />
          </div>

          <div className="product-page__summary-image">
            <ProductPageImage />
          </div>
          <div className="product-page__summary-main">
            <div className="product-page__row">
              <Readonly.Root
                className="product-page__variant-key"
                isLoading={productData.productInformation?.isLoading}
              >
                <Icon className="fg-subtle" name={IconName.CONFIGURATION} />
                <Readonly.Label>
                  <span>
                    {t("Product.Configuration.Configuration")} /{" "}
                    {t("Product.VariantKey")}
                  </span>
                </Readonly.Label>
                <ProductPageVariantKeyDisplay
                  title={productData.productInformation?.data?.title}
                  variantKey={productData.productInformation?.data?.variantKey}
                  addons={productData.productInformation?.data?.addons}
                />
              </Readonly.Root>
            </div>
            {/* <div className="product-page__row">
              <Readonly.Root
                className="product-page__delivery-time"
                isLoading={productData.productInformation?.isLoading}
              >
                <Icon className="fg-subtle" name={IconName.DELIVERY} />
                <Readonly.Label>
                  {t("Product.Delivery.DeliveryTime")}
                </Readonly.Label>
                <Readonly.Value>
                  <span>{formattedDeliveryTime}</span>
                </Readonly.Value>
              </Readonly.Root>
            </div> */}

            <div className="product-page__row">
              <Readonly.Root
                className="product-page__price"
                isLoading={productData.productInformation?.isLoading}
              >
                <Icon className="fg-subtle" name={IconName.EURO} />
                <Readonly.Label>{t("Product.GrossPrice")}</Readonly.Label>
                <Readonly.Value>
                  <FormattedPrice
                    price={productData.productInformation?.data?.price}
                    currency={DEFAULT_PRICE_CURRENCY}
                    isPriceServiceAvailable={
                      !productData.productInformation?.error
                    }
                  />
                  <div className="product-page-price__price-info">
                    <InfoTip
                      label={t("Price.ShowPriceInfo")}
                      content={
                        exists(price)
                          ? t("Price.PriceTag.Tooltip")
                          : t("Price.PriceTag.PriceUnavailable")
                      }
                      variant={exists(price) ? "warning" : "default"}
                      className="btn--bleed"
                    />
                  </div>
                </Readonly.Value>
              </Readonly.Root>
              <Readonly.Root
                className="product-page__price-group"
                isLoading={productData.productInformation?.isLoading}
              >
                <Readonly.Label>{t("Price.PriceGroup")}</Readonly.Label>
                <Readonly.Value>
                  <FormattedPriceGroups
                    groups={productData.productInformation?.data?.priceGroup}
                    isPriceServiceAvailable={
                      !productData.productInformation?.error
                    }
                  />
                </Readonly.Value>
              </Readonly.Root>
            </div>
          </div>
          <div className="product-page__actions">
            <ProductPageConfigureButton />
            <ProductPageDimensionButton />
            <ProductPageDownloadButton />
          </div>
        </section>
      )
    }
  )
)
